import React, { useState } from "react";
import { PageHeader } from "../components/layout/Shell";
import StatutoryShell from "../components/statutory/StatutoryShell";
import { StatutoryProvider, useStatutory } from "../components/statutory/StatutoryContext";
import { StatutoryBadge, StatutoryStatePanel } from "../components/statutory/StatutoryBadge";
import { eligibilityDeterminations } from "../mock/statutory/mockData";
import { ChevronRight, Lock } from "lucide-react";
import { toast } from "sonner";

const LIFECYCLE_STATES = [
  "PENDING_CONTEXT", "EVIDENCE_READY", "CLASSIFIED",
  "ELIGIBLE", "APPROVED", "EFFECTIVE",
];

function LifecycleBar({ currentState, nonSuccessState }) {
  const idx = LIFECYCLE_STATES.indexOf(currentState);
  return (
    <div className="flex items-center gap-0" data-testid="lifecycle-bar">
      {LIFECYCLE_STATES.map((state, i) => {
        const reached = idx >= i;
        const isCurrent = idx === i;
        return (
          <React.Fragment key={state}>
            <div className="flex flex-col items-center gap-1">
              <div
                className={`h-3 w-3 rounded-full border-2 transition-colors ${
                  reached ? "bg-primary border-primary" : "bg-background border-muted-foreground/30"
                } ${isCurrent ? "ring-2 ring-primary ring-offset-1" : ""}`}
              />
              <span className="text-[9px] text-muted-foreground hidden md:block" style={{ maxWidth: 60, textAlign: "center" }}>
                {state.replace(/_/g, " ")}
              </span>
            </div>
            {i < LIFECYCLE_STATES.length - 1 && (
              <div className={`flex-1 h-0.5 mx-0.5 ${reached && idx > i ? "bg-primary" : "bg-muted"}`} style={{ minWidth: 12 }} />
            )}
          </React.Fragment>
        );
      })}
      {nonSuccessState && (
        <div className="ml-3">
          <StatutoryBadge state={nonSuccessState} />
        </div>
      )}
    </div>
  );
}

function DeterminationDetail({ det, onApprove }) {
  const { currentRole } = useStatutory();
  const isBlocked = !!det.nonSuccessState && det.nonSuccessState !== "NO_APPLICABLE_REGIME";
  const isSuperseeded = det.lifecycleState === "SUPERSEDED";

  return (
    <div className="space-y-4" data-testid={`determination-detail-${det.id}`}>
      {isSuperseeded && (
        <StatutoryStatePanel
          state="SUPERSEDED"
          reason={`Superseded by determination ${det.supersededById}. Preserved as immutable audit history — read-only.`}
        />
      )}
      {isBlocked && (
        <StatutoryStatePanel
          state={det.nonSuccessState}
          oqRef={det.blockingOQ}
          suspensionNotice={true}
        />
      )}

      {/* Lifecycle */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Eligibility Lifecycle</div>
        <LifecycleBar currentState={det.lifecycleState === "SUPERSEDED" ? "EFFECTIVE" : det.lifecycleState} nonSuccessState={det.nonSuccessState} />
      </div>

      {/* Detail fields */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Determination Record</div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
          {[
            ["ID", det.id],
            ["Person", det.personName],
            ["Regime", det.regime || "—"],
            ["Eligibility Result", det.eligibilityResult],
            ["Reason Code", det.reasonCode],
            ["Effective Date", det.effectiveDate || "—"],
            ["Determined By", det.determinedBy || "—"],
            ["Determined At", det.determinedAt],
            ["Evidence Ref", det.evidenceRef || "—"],
          ].map(([k, v]) => (
            <div key={k} className="col-span-1">
              <dt className="text-[11px] text-muted-foreground">{k}</dt>
              <dd className="text-xs font-mono text-foreground mt-0.5 truncate">{v}</dd>
            </div>
          ))}
        </dl>
      </div>

      {/* Actions */}
      {!isSuperseeded && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => {
              if (!currentRole.canApprove || isBlocked) {
                toast.error(isBlocked ? `Cannot approve — ${det.nonSuccessState} state must be resolved first.` : "Insufficient permissions.");
              } else {
                toast.success("Prototype: Approve action recorded (no backend).");
              }
            }}
            className={`text-sm px-3 py-1.5 rounded border transition-colors ${isBlocked || !currentRole.canApprove ? "opacity-40 cursor-not-allowed border-border text-muted-foreground" : "border-primary text-primary hover:bg-primary/5"}`}
            title={isBlocked ? `Cannot approve while ${det.nonSuccessState} — resolve Open Question first` : ""}
            data-testid={`approve-btn-${det.id}`}
          >
            {isBlocked && <Lock className="h-3 w-3 inline mr-1" />}
            Approve
          </button>
          <button
            onClick={() => toast.info("Prototype: Request review recorded (no backend).")}
            className="text-sm px-3 py-1.5 rounded border border-border text-foreground hover:bg-muted transition-colors"
            data-testid={`review-btn-${det.id}`}
          >
            Request Review
          </button>
        </div>
      )}
      {isSuperseeded && (
        <p className="text-xs text-muted-foreground italic">
          Superseded records are immutable. No actions available. Preserved as permanent audit history.
        </p>
      )}
    </div>
  );
}

function EligibilityContent() {
  const [selected, setSelected] = useState(eligibilityDeterminations[0]);
  const active = eligibilityDeterminations.filter((d) => d.lifecycleState !== "SUPERSEDED");
  const superseded = eligibilityDeterminations.filter((d) => d.lifecycleState === "SUPERSEDED");
  const [showSuperseded, setShowSuperseded] = useState(false);

  const displayList = showSuperseded ? eligibilityDeterminations : active;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Queue */}
      <div className="lg:col-span-2 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Approval Queue</div>
          <button
            onClick={() => setShowSuperseded(!showSuperseded)}
            className="text-xs text-muted-foreground hover:text-foreground underline"
            data-testid="toggle-superseded"
          >
            {showSuperseded ? "Hide superseded" : `Show superseded (${superseded.length})`}
          </button>
        </div>
        {displayList.map((det) => (
          <button
            key={det.id}
            onClick={() => setSelected(det)}
            className={`w-full text-left rounded-lg border p-3 transition-colors ${selected?.id === det.id ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-muted/40"}`}
            data-testid={`queue-item-${det.id}`}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium">{det.personName}</span>
              <span className="text-xs font-mono text-muted-foreground">{det.id}</span>
            </div>
            <div className="text-xs text-muted-foreground mb-1.5">{det.regime || "No regime"}</div>
            <StatutoryBadge state={det.nonSuccessState || det.lifecycleState} />
          </button>
        ))}
      </div>

      {/* Detail */}
      <div className="lg:col-span-3">
        <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Determination Detail</div>
        {selected ? <DeterminationDetail det={selected} /> : <p className="text-sm text-muted-foreground">Select a determination from the queue.</p>}
      </div>
    </div>
  );
}

export default function StatutoryEligibility() {
  return (
    <StatutoryProvider>
      <PageHeader eyebrow="Statutory Configuration" title="Eligibility Determinations" description="Statutory eligibility lifecycle — mock prototype." />
      <StatutoryShell>
        <EligibilityContent />
      </StatutoryShell>
    </StatutoryProvider>
  );
}
